import React from "react";
import AdminNavbar from "../Navbars/AdminNavbar.js";
import PortalSidebar from "./PortalSidebar";
import { PortalApis } from "../../apis/PortalApis";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Oval } from 'react-loader-spinner';
import { FaPlug, FaPlus, FaTrash, FaEdit, FaPlay } from "react-icons/fa";


export default function PortalWebhooks() {

    const [webhooks, setWebhooks] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [editingId, setEditingId] = React.useState<any>(null);
    const [actionLoader, setActionLoader] = React.useState<boolean>(false);
    const [testingId, setTestingId] = React.useState<any>(null);

    const [form, setForm] = React.useState({
        url: '',
        events: ['shipment.created', 'shipment.status_changed'],
        secret: '',
    });

    const availableEvents = [
        'shipment.created', 'shipment.status_changed', 'shipment.delivered',
        'shipment.cancelled', 'tracking.updated', 'payment.received',
    ];

    React.useEffect(() => { loadWebhooks(); }, []);

    const loadWebhooks = () => {
        setLoading(true);
        PortalApis.getWebhooks().then(
            (response: any) => {
                setLoading(false);
                if (response?.data?.data) setWebhooks(response.data.data);
            }
        ).catch(function (error) {
            setLoading(false);
        });
    };

    const handleEventToggle = (event: string) => {
        setForm(prev => ({
            ...prev,
            events: prev.events.includes(event) ? prev.events.filter(e => e !== event) : [...prev.events, event]
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setActionLoader(true);

        const promise = editingId
            ? PortalApis.updateWebhook(editingId, form)
            : PortalApis.createWebhook(form);

        promise.then((response: any) => {
            setActionLoader(false);
            if (response?.data?.success) {
                toast.success(editingId ? 'Webhook updated' : 'Webhook created');
                setShowModal(false);
                setEditingId(null);
                setForm({ url: '', events: ['shipment.created', 'shipment.status_changed'], secret: '' });
                loadWebhooks();
            } else {
                toast.warn(response?.data?.message || 'Failed');
            }
        }).catch((error: any) => {
            setActionLoader(false);
            toast.error(error?.response?.data?.message || 'Error saving webhook');
        });
    };

    const handleDelete = (id: any) => {
        if (!window.confirm('Are you sure you want to delete this webhook?')) return;
        PortalApis.deleteWebhook(id).then((response: any) => {
            if (response?.data?.success) {
                toast.success('Webhook deleted');
                loadWebhooks();
            }
        }).catch(() => toast.error('Failed to delete'));
    };

    const handleTest = (id: any) => {
        setTestingId(id);
        PortalApis.testWebhook(id).then((response: any) => {
            setTestingId(null);
            if (response?.data?.success) {
                toast.success('Test webhook sent! Check your endpoint.');
            } else {
                toast.warn(response?.data?.message || 'Test failed');
            }
        }).catch((error: any) => {
            setTestingId(null);
            toast.error(error?.response?.data?.message || 'Test failed');
        });
    };

    const openEdit = (webhook: any) => {
        setEditingId(webhook.id);
        setForm({ url: webhook.url, events: webhook.events || [], secret: '' });
        setShowModal(true);
    };

    return (
        <>
            <PortalSidebar title="Webhooks" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Webhooks" />
                <div className='lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen'>

                    <div className="flex justify-between items-center lg:pt-5 pt-3">
                        <div>
                            <h3 className="text-[20px] font-semibold">Webhook Endpoints</h3>
                            <p className="text-[13px] font-normal text-[#999999]">Configure URLs to receive real-time notifications</p>
                        </div>
                        <button onClick={() => { setEditingId(null); setForm({ url: '', events: ['shipment.created', 'shipment.status_changed'], secret: '' }); setShowModal(true); }}
                            className="bg-[#E04343] text-[#ffffff] py-[10px] px-[20px] text-[14px] rounded-[5px] flex items-center gap-2">
                            <FaPlus size={14} /> Add Webhook
                        </button>
                    </div>

                    <div className="mt-6 space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-16">
                                <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                            </div>
                        ) : webhooks.length === 0 ? (
                            <div className="bg-white rounded-[10px] border border-[#E2E8F0] p-10 text-center">
                                <FaPlug size={40} className="mx-auto text-gray-300 mb-3" />
                                <p className="text-[14px] text-[#808080]">No webhooks configured yet.</p>
                                <p className="text-[12px] text-[#AAAAAA] mt-1">Add a webhook endpoint to receive real-time notifications when events occur.</p>
                            </div>
                        ) : (
                            webhooks.map((wh: any) => (
                                <div key={wh.id} className="bg-white rounded-[10px] border border-[#E2E8F0] px-[26px] py-[20px]">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <FaPlug className="text-[#0EBC93]" size={14} />
                                                <code className="text-[14px] font-mono text-[#263238]">{wh.url}</code>
                                                {wh.is_active ?
                                                    <b style={{ fontWeight: "500", fontSize: '12px', backgroundColor: '#C9F0D0', color: '#51CF66', borderRadius: '10px', padding: '2px 10px' }}>Active</b> :
                                                    <b style={{ fontWeight: "500", fontSize: '12px', backgroundColor: '#FFDDBD', color: '#FF922B', borderRadius: '10px', padding: '2px 10px' }}>Disabled</b>
                                                }
                                            </div>
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {(wh.events || []).map((ev: string) => (
                                                    <span key={ev} className="bg-[#F6F9FF] text-[#4979ED] text-[10px] px-2 py-0.5 rounded-full border border-[#D9D9D9]">{ev}</span>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => handleTest(wh.id)} disabled={testingId === wh.id}
                                                className="bg-[#0EBC93] text-white p-2 rounded-[5px] text-[12px] flex items-center gap-1 hover:opacity-80">
                                                {testingId === wh.id ? <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#FCBF94" /> : <FaPlay size={12} />}
                                                Test
                                            </button>
                                            <button onClick={() => openEdit(wh)} className="bg-[#F6F9FF] text-[#4979ED] p-2 rounded-[5px] hover:bg-blue-100">
                                                <FaEdit size={14} />
                                            </button>
                                            <button onClick={() => handleDelete(wh.id)} className="bg-[#FF3B300D] text-[#E04343] p-2 rounded-[5px] hover:bg-red-100">
                                                <FaTrash size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[10px] w-full max-w-[500px] shadow-xl">
                        <div className="flex justify-between items-center px-5 py-4 border-b">
                            <h3 className="text-[16px] font-semibold">{editingId ? 'Edit Webhook' : 'Add Webhook'}</h3>
                            <button onClick={() => setShowModal(false)} className="text-[#808080] hover:text-black text-[20px]">&times;</button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-5 space-y-4">
                            <div>
                                <label className="block text-[#333333] text-[12px] mb-2">Webhook URL <span className="text-red-400">*</span></label>
                                <input type="url" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                                    className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                    placeholder="https://your-server.com/webhooks/skynet" />
                            </div>
                            <div>
                                <label className="block text-[#333333] text-[12px] mb-2">Signing Secret (Optional)</label>
                                <input type="text" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })}
                                    className="border border-[#D1D1D1] text-gray-900 text-sm rounded-[5px] block w-full pl-4 p-2.5"
                                    placeholder="Leave empty to auto-generate" />
                            </div>
                            <div>
                                <label className="block text-[#333333] text-[12px] mb-2">Events</label>
                                <div className="flex flex-wrap gap-2">
                                    {availableEvents.map(event => (
                                        <button key={event} type="button" onClick={() => handleEventToggle(event)}
                                            className={`text-[12px] px-3 py-1.5 rounded-full border ${form.events.includes(event) ? 'bg-[#E04343] text-white border-[#E04343]' : 'bg-white text-[#808080] border-[#D1D1D1]'}`}>
                                            {event}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <button type="submit" disabled={actionLoader}
                                className="text-white bg-[#E04343] rounded-[5px] py-[10px] px-[42px] font-[600] text-[14px] w-full flex justify-center gap-2">
                                {editingId ? 'Update Webhook' : 'Create Webhook'}
                                <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={true} />
        </>
    );
}